export interface SearchableClinic {
  name: string;
  hospitalId?: number | null;
  departmentIds?: number[];
}

// Lowercase, drop punctuation, and collapse whitespace so "St. Mary's" and
// "st  marys" compare equal.
export const normalizeSearchText = (text: string): string =>
  text
    .toLowerCase()
    .replace(/[^\p{L}\p{N}\s]/gu, "")
    .replace(/\s+/g, " ")
    .trim();

/**
 * Ranks how well a clinic matches a search query; lower is better, null is no
 * match (including an empty query). 0 = exact name or hospital/department ID,
 * 1 = name starts with the query, 2 = every query word is a substring of the
 * name or IDs.
 */
export const rankClinicMatch = (
  query: string,
  clinic: SearchableClinic,
): number | null => {
  const normalizedQuery = normalizeSearchText(query);
  if (!normalizedQuery) {
    return null;
  }
  const ids = [clinic.hospitalId, ...(clinic.departmentIds ?? [])]
    .filter((id): id is number => id != null)
    .map(String);
  const name = normalizeSearchText(clinic.name);

  if (name === normalizedQuery || ids.includes(normalizedQuery)) {
    return 0;
  }
  if (name.startsWith(normalizedQuery)) {
    return 1;
  }
  const searchable = [name, ...ids].join(" ");
  if (normalizedQuery.split(" ").every((word) => searchable.includes(word))) {
    return 2;
  }
  return null;
};

/** Drops items ranked null and orders the rest by rank, stable within a rank. */
export const sortByMatchRank = <T>(
  items: T[],
  rank: (item: T) => number | null,
): T[] =>
  items
    .map((item) => ({ item, rank: rank(item) }))
    .filter(({ rank }) => rank !== null)
    .sort((a, b) => a.rank! - b.rank!)
    .map(({ item }) => item);

/** Returns the clinics matching the query, best match first. */
export const searchClinics = <T>(
  query: string,
  items: T[],
  toClinic: (item: T) => SearchableClinic,
): T[] =>
  sortByMatchRank(items, (item) => rankClinicMatch(query, toClinic(item)));
